/**
 * The icon set.
 *
 * Inline SVG path data rather than an icon font or a remote sprite. Three reasons:
 * an icon that arrives over the network can be blocked by a CSP or a corporate proxy
 * and leaves a hole in the layout; an icon font renders as a tofu box when it fails;
 * and inline paths inherit `currentColor`, so a single icon works in every semantic
 * colour without a second asset.
 *
 * All paths are drawn on a 24x24 grid with a 1.75 stroke, rounded caps and joins, so
 * the set reads as one family rather than as borrowed pieces.
 */

export type IconName = keyof typeof ICON_PATHS;

export const ICON_PATHS = {
  // --- navigation & structure ---
  dashboard:
    'M4 5.5A1.5 1.5 0 0 1 5.5 4h4A1.5 1.5 0 0 1 11 5.5v3A1.5 1.5 0 0 1 9.5 10h-4A1.5 1.5 0 0 1 4 8.5v-3ZM13 5.5A1.5 1.5 0 0 1 14.5 4h4A1.5 1.5 0 0 1 20 5.5v7a1.5 1.5 0 0 1-1.5 1.5h-4a1.5 1.5 0 0 1-1.5-1.5v-7ZM4 13.5A1.5 1.5 0 0 1 5.5 12h4a1.5 1.5 0 0 1 1.5 1.5v5A1.5 1.5 0 0 1 9.5 20h-4A1.5 1.5 0 0 1 4 18.5v-5ZM13 17.5a1.5 1.5 0 0 1 1.5-1.5h4a1.5 1.5 0 0 1 1.5 1.5v1a1.5 1.5 0 0 1-1.5 1.5h-4a1.5 1.5 0 0 1-1.5-1.5v-1Z',
  pods: 'M12 3.2 20 7.6v8.8L12 20.8 4 16.4V7.6l8-4.4ZM4 7.6l8 4.4 8-4.4M12 12v8.8',
  gpu: 'M3 7.5A1.5 1.5 0 0 1 4.5 6h15A1.5 1.5 0 0 1 21 7.5v7a1.5 1.5 0 0 1-1.5 1.5h-15A1.5 1.5 0 0 1 3 14.5v-7ZM7 16v2M17 16v2M7.5 9.5h4v3h-4zM15 9.5h1.5M15 12h1.5',
  server:
    'M4 5.5A1.5 1.5 0 0 1 5.5 4h13A1.5 1.5 0 0 1 20 5.5v3A1.5 1.5 0 0 1 18.5 10h-13A1.5 1.5 0 0 1 4 8.5v-3ZM4 15.5A1.5 1.5 0 0 1 5.5 14h13a1.5 1.5 0 0 1 1.5 1.5v3a1.5 1.5 0 0 1-1.5 1.5h-13A1.5 1.5 0 0 1 4 18.5v-3ZM7.5 7h.01M7.5 17h.01M11 7h3M11 17h3',
  storage:
    'M4 6.5c0-1.38 3.58-2.5 8-2.5s8 1.12 8 2.5-3.58 2.5-8 2.5-8-1.12-8-2.5ZM4 6.5v11c0 1.38 3.58 2.5 8 2.5s8-1.12 8-2.5v-11M4 12c0 1.38 3.58 2.5 8 2.5s8-1.12 8-2.5',
  network:
    'M12 3v5M12 16v5M4.9 7.5 9 10M15 14l4.1 2.5M19.1 7.5 15 10M9 14l-4.1 2.5M12 10.5a1.5 1.5 0 1 0 0 3 1.5 1.5 0 0 0 0-3ZM12 3.5a1.5 1.5 0 1 0 0-.01M4 8.5a1.5 1.5 0 1 0 0-.01M20 8.5a1.5 1.5 0 1 0 0-.01M4 17.5a1.5 1.5 0 1 0 0-.01M20 17.5a1.5 1.5 0 1 0 0-.01M12 21.5a1.5 1.5 0 1 0 0-.01',
  billing:
    'M3 8.5A2.5 2.5 0 0 1 5.5 6h13A2.5 2.5 0 0 1 21 8.5v7a2.5 2.5 0 0 1-2.5 2.5h-13A2.5 2.5 0 0 1 3 15.5v-7ZM3 10h18M6.5 14.5h3',
  key: 'M14.5 4a5.5 5.5 0 1 1-4.2 9.05L9 14.3H7v2H5v2H2.5v-2.6l6.34-6.34A5.5 5.5 0 0 1 14.5 4ZM16 8.5h.01',
  users:
    'M15.5 19.5v-1.7a3.4 3.4 0 0 0-3.4-3.4H6.9a3.4 3.4 0 0 0-3.4 3.4v1.7M9.5 11.1a3.4 3.4 0 1 0 0-6.8 3.4 3.4 0 0 0 0 6.8ZM20.5 19.5v-1.7a3.4 3.4 0 0 0-2.6-3.3M15.4 4.4a3.4 3.4 0 0 1 0 6.6',
  shield:
    'M12 3.5 5 6.2v5.1c0 4.3 2.9 8.3 7 9.2 4.1-.9 7-4.9 7-9.2V6.2l-7-2.7ZM9.5 12l1.8 1.8 3.4-3.6',
  activity: 'M3.5 12.5h3.2l2.3-6.6 4 13.2 2.4-6.6h5.1',
  audit:
    'M6 3.5h8l4.5 4.5v12.5a1.5 1.5 0 0 1-1.5 1.5H6a1.5 1.5 0 0 1-1.5-1.5V5A1.5 1.5 0 0 1 6 3.5ZM13.5 3.5V8H18M8 13h8M8 16.5h5',
  settings:
    'M12 15.2a3.2 3.2 0 1 0 0-6.4 3.2 3.2 0 0 0 0 6.4ZM19.1 14.5a1.6 1.6 0 0 0 .32 1.76l.06.06a1.9 1.9 0 1 1-2.7 2.7l-.06-.06a1.6 1.6 0 0 0-1.76-.32 1.6 1.6 0 0 0-.97 1.47v.17a1.9 1.9 0 1 1-3.8 0v-.09a1.6 1.6 0 0 0-1.05-1.47 1.6 1.6 0 0 0-1.76.32l-.06.06a1.9 1.9 0 1 1-2.7-2.7l.06-.06a1.6 1.6 0 0 0 .32-1.76 1.6 1.6 0 0 0-1.47-.97H3.3a1.9 1.9 0 1 1 0-3.8h.09a1.6 1.6 0 0 0 1.47-1.05 1.6 1.6 0 0 0-.32-1.76l-.06-.06a1.9 1.9 0 1 1 2.7-2.7l.06.06a1.6 1.6 0 0 0 1.76.32h.08a1.6 1.6 0 0 0 .97-1.47V3.3a1.9 1.9 0 1 1 3.8 0v.09a1.6 1.6 0 0 0 .97 1.47 1.6 1.6 0 0 0 1.76-.32l.06-.06a1.9 1.9 0 1 1 2.7 2.7l-.06.06a1.6 1.6 0 0 0-.32 1.76v.08a1.6 1.6 0 0 0 1.47.97h.17a1.9 1.9 0 1 1 0 3.8h-.09a1.6 1.6 0 0 0-1.47.97Z',

  // --- actions ---
  plus: 'M12 5v14M5 12h14',
  play: 'M7.5 5.2v13.6a.7.7 0 0 0 1.07.6l10.6-6.8a.7.7 0 0 0 0-1.2L8.57 4.6a.7.7 0 0 0-1.07.6Z',
  stop: 'M6.5 7.5A1 1 0 0 1 7.5 6.5h9a1 1 0 0 1 1 1v9a1 1 0 0 1-1 1h-9a1 1 0 0 1-1-1v-9Z',
  restart: 'M20 11.5A8 8 0 1 1 17.5 6M20 4v5h-5',
  trash:
    'M4.5 6.5h15M9.5 6.5V5a1.5 1.5 0 0 1 1.5-1.5h2A1.5 1.5 0 0 1 14.5 5v1.5M6.5 6.5l.8 12.1a1.5 1.5 0 0 0 1.5 1.4h6.4a1.5 1.5 0 0 0 1.5-1.4l.8-12.1M10 10.5v6M14 10.5v6',
  copy: 'M9 9.5A1.5 1.5 0 0 1 10.5 8h8A1.5 1.5 0 0 1 20 9.5v9a1.5 1.5 0 0 1-1.5 1.5h-8A1.5 1.5 0 0 1 9 18.5v-9ZM6 16H5.5A1.5 1.5 0 0 1 4 14.5v-9A1.5 1.5 0 0 1 5.5 4h9A1.5 1.5 0 0 1 16 5.5V6',
  check: 'M5 12.5 9.5 17 19 7',
  close: 'M6 6l12 12M18 6 6 18',
  search: 'M11 18a7 7 0 1 0 0-14 7 7 0 0 0 0 14ZM20 20l-4-4',
  filter: 'M4 6.5h16M7 12h10M10 17.5h4',
  refresh: 'M20.5 11.5A8.5 8.5 0 0 0 6 6M3.5 12.5A8.5 8.5 0 0 0 18 18M3.5 5.5v5h5M20.5 18.5v-5h-5',
  download: 'M12 4v11M7.5 10.5 12 15l4.5-4.5M4.5 19.5h15',
  external:
    'M14 4h6v6M20 4l-8.5 8.5M18 14v4.5a1.5 1.5 0 0 1-1.5 1.5h-11A1.5 1.5 0 0 1 4 18.5v-11A1.5 1.5 0 0 1 5.5 6H10',
  terminal:
    'M5 6.5A1.5 1.5 0 0 1 6.5 5h11A1.5 1.5 0 0 1 19 6.5v11a1.5 1.5 0 0 1-1.5 1.5h-11A1.5 1.5 0 0 1 5 17.5v-11ZM8.5 9.5l2.5 2.5-2.5 2.5M13 15h3',
  logout:
    'M14.5 16.5V19a1.5 1.5 0 0 1-1.5 1.5H6A1.5 1.5 0 0 1 4.5 19V5A1.5 1.5 0 0 1 6 3.5h7a1.5 1.5 0 0 1 1.5 1.5v2.5M10 12h10M17 9l3 3-3 3',

  // --- status & feedback ---
  info: 'M12 20.5a8.5 8.5 0 1 0 0-17 8.5 8.5 0 0 0 0 17ZM12 11v5.5M12 7.8h.01',
  warning:
    'M10.7 4.2 2.9 17.5A1.5 1.5 0 0 0 4.2 19.8h15.6a1.5 1.5 0 0 0 1.3-2.3L13.3 4.2a1.5 1.5 0 0 0-2.6 0ZM12 9.5v4M12 16.8h.01',
  error: 'M12 20.5a8.5 8.5 0 1 0 0-17 8.5 8.5 0 0 0 0 17ZM9.2 9.2l5.6 5.6M14.8 9.2l-5.6 5.6',
  success: 'M12 20.5a8.5 8.5 0 1 0 0-17 8.5 8.5 0 0 0 0 17ZM8.3 12.2l2.6 2.6 4.8-5.2',
  clock: 'M12 20.5a8.5 8.5 0 1 0 0-17 8.5 8.5 0 0 0 0 17ZM12 7.5V12l3 1.8',
  spark: 'M12 3.5 14.2 9l5.8.5-4.4 3.9 1.3 5.7-4.9-3.1-4.9 3.1L8.4 13.4 4 9.5 9.8 9 12 3.5Z',
  bolt: 'M13.5 3.5 5.5 13.5h5l-1 7 8-10h-5l1-7Z',
  chip: 'M8 8h8v8H8zM9 3.5v2M12 3.5v2M15 3.5v2M9 18.5v2M12 18.5v2M15 18.5v2M3.5 9h2M3.5 12h2M3.5 15h2M18.5 9h2M18.5 12h2M18.5 15h2M6.5 6.5A1 1 0 0 1 7.5 5.5h9a1 1 0 0 1 1 1v11a1 1 0 0 1-1 1h-9a1 1 0 0 1-1-1v-11Z',
  chevronRight: 'M9.5 5.5 16 12l-6.5 6.5',
  chevronDown: 'M5.5 9.5 12 16l6.5-6.5',
  chevronLeft: 'M14.5 5.5 8 12l6.5 6.5',
  arrowRight: 'M4.5 12h15M13.5 6l6 6-6 6',
  menu: 'M4 7h16M4 12h16M4 17h16',
  sun: 'M12 16.2a4.2 4.2 0 1 0 0-8.4 4.2 4.2 0 0 0 0 8.4ZM12 2.5v2M12 19.5v2M4.2 4.2l1.4 1.4M18.4 18.4l1.4 1.4M2.5 12h2M19.5 12h2M4.2 19.8l1.4-1.4M18.4 5.6l1.4-1.4',
  moon: 'M20.5 14.3A8.5 8.5 0 1 1 9.7 3.5a6.6 6.6 0 0 0 10.8 10.8Z',
  book: 'M4 5.5A1.5 1.5 0 0 1 5.5 4H10a3 3 0 0 1 2 5.2V20a3 3 0 0 0-2-.8H5.5A1.5 1.5 0 0 1 4 17.7V5.5ZM20 5.5A1.5 1.5 0 0 0 18.5 4H14a3 3 0 0 0-2 5.2V20a3 3 0 0 1 2-.8h4.5a1.5 1.5 0 0 0 1.5-1.5V5.5Z',
  rocket:
    'M8.5 15.5 6 21l5.5-2.5M8.5 15.5 5.9 12.9a1 1 0 0 1 0-1.4l2.5-2.5a9.5 9.5 0 0 1 8.4-2.6l2.3.4.4 2.3a9.5 9.5 0 0 1-2.6 8.4l-2.5 2.5a1 1 0 0 1-1.4 0l-2.6-2.6M14.5 9.5h.01',
} as const;

export const ICON_NAMES = Object.keys(ICON_PATHS) as IconName[];

/** Icons that read better filled than stroked. */
export const FILLED_ICONS = new Set<IconName>(['play', 'stop', 'bolt', 'spark']);
